import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOccupationResolver } from '@/hooks/useOccupationResolver';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, BarChart3, Target, Users, HelpCircle, Zap } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import PageBreadcrumb from '@/components/shared/PageBreadcrumb';

import { generateOccupationSchema, generateOrganizationSchema, generateMultipleSchemas, generateWebPageSchema } from '@/lib/schema';
import AdSlot from '@/components/shared/AdSlot';
import { useTrendsAdvanced } from '@/hooks/useTrendsAdvanced';
import { useMultiOccupationCompare } from '@/hooks/useMultiOccupationCompare';
import { useHistoricalDataLimits } from '@/hooks/useHistoricalDataLimits';
import VolatilityDashboard from '@/components/trends/VolatilityDashboard';
import MultiOccupationSelector from '@/components/trends/MultiOccupationSelector';
import WatchlistManager from '@/components/trends/WatchlistManager';
import CareerProjections from '@/components/trends/CareerProjections';
import { PercentileBreakdown } from '@/components/trends/PercentileBreakdown';
import { GeographicHeatmap } from '@/components/trends/GeographicHeatmap';
import { EconomicContext } from '@/components/trends/EconomicContext';
import { InteractiveTimeseries } from '@/components/trends/InteractiveTimeseries';
import { EmploymentTrends } from '@/components/trends/EmploymentTrends';
import TrendsLanding from '@/components/trends/TrendsLanding';
import OccupationLink from '@/components/shared/OccupationLink';
import MetricNavigation from '@/components/shared/MetricNavigation';
import RelatedMetrics from '@/components/shared/RelatedMetrics';
import CareerAnniversaryInsights from '@/components/trends/CareerAnniversaryInsights';
import TrendsContext from '@/components/content/TrendsContext';
import { toTitleCase, formatLocationName } from '@/lib/utils';
import { getAreaCodeForLocation } from '@/lib/stateUtils';
import { shouldIndexTrendsPage } from '@/lib/seoIndexing';
import { useAreaCodeResolver } from '@/hooks/useAreaCodeResolver';

const TrendsCenter = () => {
  const { occupation, location } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startYear, endYear, isRestricted, description } = useHistoricalDataLimits();
  const [selectedOccupations, setSelectedOccupations] = useState<Array<{code: string; title: string}>>([]);
  const [geoMetric, setGeoMetric] = useState<'salary' | 'employment' | 'growth'>('salary');
  const [activeTab, setActiveTab] = useState('overview');

  const trendTabs = [
    { value: 'overview', label: 'Overview', icon: TrendingUp },
    { value: 'employment', label: 'Employment', icon: Users },
    { value: 'analytics', label: 'Analytics', icon: BarChart3 },
    { value: 'projections', label: 'Projections', icon: Target },
    { value: 'compare', label: 'Compare', icon: Users },
  ] as const;

  // Check if URL uses SOC code format (XX-XXXX-description) first
  const socCodeMatch = occupation?.match(/^(\d{2}-\d{4})/);
  const extractedSOCCode = socCodeMatch ? socCodeMatch[1] : '';
  const isSOCFormat = !!extractedSOCCode;
  
  // Resolve occupation from URL parameter - use SOC code if detected, otherwise use full slug
  const occupationToResolve = isSOCFormat ? extractedSOCCode : (occupation || '');
  const { data: occupationData, isLoading: occupationLoading, error: occupationError } = useOccupationResolver(occupationToResolve);
  
  // Extract resolved occupation data
  const occupation_code = occupationData?.soc || '';
  const occupation_title = occupationData?.title || '';
  
  // If it's SOC format, extract title from the rest of the slug
  const fallback_occupation_code = extractedSOCCode;
  const fallback_occupation_title = isSOCFormat && occupation 
    ? occupation.replace(/^\d{2}-\d{4}-?/, '').replace(/-/g, ' ').replace(/_/g, ' ')
    : '';
  
  // Use resolved data or fallback for legacy URLs
  const final_occupation_code = occupation_code || fallback_occupation_code;
  const final_occupation_title = occupation_title || (fallback_occupation_title ? toTitleCase(fallback_occupation_title) : '');
  
  // Resolve area code for location (handles states, MSAs, and national)
  const { data: resolvedAreaCode } = useAreaCodeResolver(location || '');
  const area_code = resolvedAreaCode || (location === 'united-states' ? '99' : getAreaCodeForLocation(location || '') || '99');
  const area_name = formatLocationName(location || '');

  // Determine if we're in landing mode (no parameters) or analysis mode
  const isLandingMode = !occupation || (!final_occupation_code && !occupationLoading);

  // Fetch advanced trends data (only in analysis mode)
  const { data: trendsData, isLoading: trendsLoading, error: trendsError } = useTrendsAdvanced(
    {
      occupation_codes: [final_occupation_code || ''],
      area: area_code || '',
      start_year: startYear,
      end_year: endYear,
      include_projections: true
    }
  );

  // Fetch multi-occupation comparison data (only in analysis mode)
  const { data: compareData, isLoading: compareLoading } = useMultiOccupationCompare(
    {
      primary_occupation: final_occupation_code || '',
      comparison_occupations: selectedOccupations.map(occ => occ.code),
      area: area_code || '',
      user_id: user?.id
    }
  );

  // Get the main trend analysis for the primary occupation (most recent year)
  const trendAnalysis = trendsData?.trends_analysis
    ?.filter(t => t.occupation_code === final_occupation_code)
    ?.sort((a, b) => (b.year || 0) - (a.year || 0))?.[0];

  // Fetch geographic data for selected occupations (must be before early returns)
  const { data: geographicData, isLoading: geoLoading } = useQuery({
    queryKey: ['geographic-occupation', selectedOccupations.map(o => o.code).join(','), final_occupation_code],
    queryFn: async () => {
      const occupationCodes = selectedOccupations.length > 0 
        ? selectedOccupations.map(o => o.code)
        : (final_occupation_code ? [final_occupation_code] : []);
      
      if (occupationCodes.length === 0) {
        return [];
      }

      // No year: the edge function resolves the latest available OEWS year,
      // so the map advances automatically with each annual data refresh.
      const { data, error } = await supabase.functions.invoke('api-geographic-occupation', {
        body: {
          occupation_codes: occupationCodes
        }
      });
      
      if (error) throw error;
      return data?.states || [];
    },
    enabled: (!!final_occupation_code || selectedOccupations.length > 0),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Generate title from URL params (always available, no API needed)
  const urlDerivedOccupationTitle = occupation && !isSOCFormat
    ? toTitleCase(occupation.replace(/-/g, ' ').replace(/\d{2}-\d{4}-?/g, ''))
    : final_occupation_title || 'Occupation';
  
  // Use API data when available, fallback to URL-derived
  const occupationTitle = final_occupation_title || urlDerivedOccupationTitle;
  
  const seoTitle = isLandingMode 
    ? `Market Trends & Career Intelligence | WageAtlas`
    : `${occupationTitle} Salary Trends in ${area_name} | WageAtlas`;
  const seoDescription = isLandingMode
    ? `Explore comprehensive market trends, salary analytics, and career intelligence. Access professional-grade occupation insights and economic analysis.`
    : `Analyze ${occupationTitle} salary trends and employment data in ${area_name}. Get historical insights, growth projections, and career recommendations.`;

  // Generate schemas - always include at least Organization and WebPage
  const baseUrl = 'https://wageatlas.com';
  const canonicalUrl = !isLandingMode && occupation
    ? `${baseUrl}/trends/${occupation}/${location || 'united-states'}/`
    : `${baseUrl}/trends/`;
  
  // Landing page stays indexed; detail pages only for the curated subset while
  // the AdSense "low value content" review is pending (see src/lib/seoIndexing.ts)
  const isIndexable = isLandingMode || shouldIndexTrendsPage(occupation, location);

  const organizationSchema = generateOrganizationSchema(baseUrl);
  const webPageSchema = generateWebPageSchema(
    seoTitle,
    seoDescription,
    canonicalUrl,
    {
      occupation: !isLandingMode ? occupationTitle : undefined,
      location: !isLandingMode ? area_name : undefined,
      baseUrl,
    }
  );
  
  // Get latest wage data for occupation schema (optional)
  const latestWageData = trendsData?.wage_history
    ?.filter(wh => wh.occ_code === final_occupation_code)
    ?.sort((a, b) => (b.year || 0) - (a.year || 0))?.[0];
  
  // Always generate occupation schema (salary data is optional)
  const occupationSchema = !isLandingMode && occupationTitle
    ? generateOccupationSchema(
        occupationTitle,
        area_name,
        latestWageData ? {
          median: latestWageData.a_median || latestWageData.a_mean || 0,
          mean: latestWageData.a_mean || 0,
          min: latestWageData.a_p10 || latestWageData.a_mean * 0.6 || 0,
          max: latestWageData.a_p90 || latestWageData.a_mean * 1.3 || 0,
          p10: latestWageData.a_p10,
          p25: latestWageData.a_p25,
          p75: latestWageData.a_p75,
          p90: latestWageData.a_p90,
          currency: 'USD',
        } : undefined
      )
    : null;
  
  // Always include at least Organization and WebPage schemas
  const schemasToInclude = [organizationSchema, webPageSchema];
  if (occupationSchema) schemasToInclude.push(occupationSchema);
  const structuredDataSchemas = generateMultipleSchemas(schemasToInclude);

  if ((trendsError || occupationError) && !isLandingMode) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">Unable to Load Trends Data</h1>
          <p className="text-muted-foreground">
            {occupationError?.message || trendsError?.message || 'There was an error loading the data. Please try again later.'}
          </p>
          <Button onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  // Always render - don't wait for loading
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        schemas={structuredDataSchemas}
        canonical={canonicalUrl}
        noIndex={!isIndexable}
      />

      {/* Hero Section for Landing Mode */}
      {isLandingMode && (
        <PageHero
          title={
            <>
              Market Trends &
              <HeroAccent> Career Intelligence</HeroAccent>
            </>
          }
          subtitle="Professional market analysis powered by 28+ years of employment data - free for everyone"
        />
      )}

      <div className={`container mx-auto px-4 py-8 space-y-6`}>
        {/* Header for Analysis Mode - Always render, use URL-derived title as fallback */}
        {!isLandingMode && (
          <div className="animate-fade-in space-y-3">
            <PageBreadcrumb
              items={[
                { label: 'Home', href: '/' },
                { label: 'Trends', href: '/trends' },
                { label: occupationTitle },
              ]}
            />
            <div>
               <h1 className="text-2xl md:text-3xl font-bold">
                 {`${occupationTitle} Trends in ${area_name}`}
               </h1>
               {final_occupation_code && (
                 <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                   SOC Code: {final_occupation_code}
                   <Link 
                     to="/what-is-soc-code"
                     className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                     title="What is a SOC code?"
                   >
                     <HelpCircle className="h-3 w-3" />
                     What is this?
                   </Link>
                 </p>
               )}
              <div className="text-muted-foreground flex items-center gap-2">
                Comprehensive salary analysis and career intelligence
              </div>
            </div>
          </div>
        )}
        
        {/* Show loading skeleton only for data-dependent content */}
        {(trendsLoading || occupationLoading) && !isLandingMode && (
          <div className="space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        )}
        
        {/* Data-dependent content - only show when loaded */}
        {!(trendsLoading || occupationLoading) && !isLandingMode && (
          <>
            {/* Historical Data Restriction Notice */}
            {isRestricted && (
              <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
                <TrendingUp className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-300">
                  {description} • Currently showing {endYear - startYear + 1} years of data ({startYear}-{endYear})
                </AlertDescription>
              </Alert>
            )}

            {/* Trends Context Content */}
            <TrendsContext 
              occupationTitle={occupationTitle}
              locationName={area_name}
            />
          </>
        )}

      {/* Main Content */}
      {isLandingMode ? (
        <TrendsLanding />
      ) : !(trendsLoading || occupationLoading) ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6 min-w-0">
              <Card className="shadow-lg min-w-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
                  {/* Mobile / narrow: dropdown avoids cramped 5-tab strip */}
                  <div className="space-y-1.5 p-2 lg:hidden">
                    <div className="flex items-center justify-between gap-2 px-0.5">
                      <Label htmlFor="trends-section" className="text-sm font-medium">
                        View section
                      </Label>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {trendTabs.findIndex((t) => t.value === activeTab) + 1} of {trendTabs.length}
                      </span>
                    </div>
                    <Select value={activeTab} onValueChange={setActiveTab}>
                      <SelectTrigger id="trends-section" aria-label="Choose trends section">
                        <SelectValue placeholder="Choose a section" />
                      </SelectTrigger>
                      <SelectContent>
                        {trendTabs.map(({ value, label, icon: Icon }) => (
                          <SelectItem key={value} value={value}>
                            <span className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="px-0.5 text-xs text-muted-foreground">
                      Tap to switch — also{' '}
                      {trendTabs
                        .filter((t) => t.value !== activeTab)
                        .map((t) => t.label)
                        .join(' · ')}
                    </p>
                  </div>

                  {/* Desktop: equal columns inside padded box — never w-full + margin */}
                  <div className="hidden min-w-0 p-2 pb-0 lg:block">
                    <TabsList className="grid h-auto w-full min-w-0 grid-cols-5 gap-1 overflow-hidden">
                      {trendTabs.map(({ value, label, icon: Icon }) => (
                        <TabsTrigger
                          key={value}
                          value={value}
                          className="min-w-0 gap-1 overflow-hidden px-1.5 text-xs xl:gap-1.5 xl:px-3 xl:text-sm"
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0 xl:h-4 xl:w-4" />
                          <span className="truncate">{label}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                <TabsContent value="overview" className="space-y-6">
                  {/* Enhanced Interactive Chart */}
                  <InteractiveTimeseries
                    wageHistory={trendsData?.wage_history || []}
                    economicContext={trendsData?.economic_context || []}
                    title="Comprehensive Salary Analysis"
                    showPercentiles={true}
                    showEmployment={true}
                    rppAdjusted={false}
                     occupationTitle={final_occupation_title}
                  />

                  {/* Percentile Breakdown for Pro Users */}
                  <PercentileBreakdown
                    data={trendsData?.wage_history || []}
                    title="Salary Distribution Analysis"
                    rppAdjusted={false}
                  />

                  {/* Economic Context Analysis */}
                  <EconomicContext
                    indicators={trendsData?.economic_context?.map(ec => ({
                      ...ec,
                      recession_flag: ec.recession_flag ? 1 : 0
                    })) || []}
                    wageHistory={trendsData?.wage_history?.map(wh => ({
                      ...wh,
                      a_median: wh.a_median || wh.a_mean
                    })) || []}
                     occupationTitle={final_occupation_title}
                    areaName={area_name}
                  />
                </TabsContent>

                <TabsContent value="employment" className="space-y-6">
                  <EmploymentTrends
                    wageHistory={trendsData?.wage_history || []}
                    economicContext={trendsData?.economic_context || []}
                     occupationTitle={final_occupation_title}
                    areaName={area_name}
                  />
                </TabsContent>

                <TabsContent value="analytics" className="space-y-6">
                  {trendAnalysis ? (
                    <VolatilityDashboard
                      trendAnalysis={{
                        ...trendAnalysis,
                        year: trendsData?.wage_history?.[trendsData.wage_history.length - 1]?.year || new Date().getFullYear(),
                        recovery_periods: typeof trendAnalysis.recovery_periods === 'string' 
                          ? parseInt(trendAnalysis.recovery_periods) || 0 
                          : trendAnalysis.recovery_periods
                      }}
                      wageHistory={trendsData?.wage_history || []}
                       occupationTitle={final_occupation_title}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading volatility analysis...
                    </div>
                  )}
                  
                  {/* Career Anniversary Insights for Pro Users */}
                  <CareerAnniversaryInsights
                     occupationTitle={final_occupation_title}
                    currentSalary={trendsData?.wage_history?.[trendsData.wage_history.length - 1]?.a_median || 0}
                    lastYearSalary={trendsData?.wage_history?.[trendsData.wage_history.length - 2]?.a_median || 0}
                    industryGrowth={trendAnalysis?.growth_5yr ? trendAnalysis.growth_5yr * 100 : 0}
                  />
                </TabsContent>

                <TabsContent value="projections" className="space-y-6">
                  <CareerProjections
                    occupation_code={final_occupation_code || ''}
                    area_code={area_code || ''}
                  />
                </TabsContent>

                <TabsContent value="compare" className="space-y-6">
                  <MultiOccupationSelector
                    selectedOccupations={selectedOccupations}
                    onSelectionChange={setSelectedOccupations}
                    primaryOccupation={{code: final_occupation_code || '', title: final_occupation_title || ''}}
                  />

                  {selectedOccupations.length > 0 && (
                    <>
                      {/* Geographic Comparison Heatmap */}
                      {geoLoading ? (
                        <Card>
                          <CardHeader>
                            <CardTitle>Multi-Region Analysis</CardTitle>
                            <CardDescription>Loading geographic data...</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="h-96 bg-muted/20 rounded-lg animate-pulse" />
                          </CardContent>
                        </Card>
                      ) : (
                        <GeographicHeatmap
                          data={geographicData || []}
                          metric={geoMetric}
                          onMetricChange={setGeoMetric}
                          title="Multi-Region Analysis"
                          occupationTitle={final_occupation_title}
                        />
                      )}

                      {compareData?.comparison_metrics && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Occupation Comparison Results</CardTitle>
                            <CardDescription>
                              Comparing {selectedOccupations.length} occupations in {area_name}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {compareData.comparison_metrics.map((metric, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                  <div className="flex justify-between items-center mb-2">
                                    <OccupationLink
                                      occupation={{
                                        occ_title: metric.occupation_title,
                                        soc: metric.occupation_code || selectedOccupations.find(occ => occ.title === metric.occupation_title)?.code
                                      }}
                                      location={location || 'united-states'}
                                      className="font-medium hover:underline"
                                    >
                                      {metric.occupation_title}
                                    </OccupationLink>
                                    <Badge variant="outline">
                                      Similarity: {metric.similarity_score != null && !isNaN(metric.similarity_score)
                                        ? `${(metric.similarity_score * 100).toFixed(1)}%`
                                        : 'N/A'}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <div className="text-muted-foreground">Median Salary</div>
                                      <div className="font-medium">
                                        {metric.median_salary != null 
                                          ? `$${metric.median_salary.toLocaleString()}` 
                                          : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-muted-foreground">Growth Rate</div>
                                      <div className={`font-medium ${metric.growth_rate >= 0 ? 'text-success' : 'text-destructive'}`}>
                                        {metric.growth_rate != null 
                                          ? `${metric.growth_rate >= 0 ? '+' : ''}${metric.growth_rate.toFixed(1)}%`
                                          : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-muted-foreground">Employment</div>
                                      <div className="font-medium">
                                        {metric.employment != null 
                                          ? metric.employment.toLocaleString() 
                                          : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-muted-foreground">Correlation</div>
                                      <div className="font-medium">
                                        {metric.correlation != null 
                                          ? metric.correlation.toFixed(2) 
                                          : 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </TabsContent>
                </Tabs>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {!isLandingMode && (
                <>
                  <MetricNavigation
                    occupation={{
                      title: final_occupation_title,
                      occ_title: final_occupation_title,
                      soc: final_occupation_code,
                      soc_code: final_occupation_code,
                    }}
                    location={location}
                    currentMetric="trends"
                    collapsible={false}
                  />
                  
                  {/* AI Risk Analysis CTA */}
                  {final_occupation_code && (
                    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Zap className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">Understand Future Automation Impact</CardTitle>
                            <CardDescription>Get AI replacement risk analysis for {final_occupation_title || 'this occupation'}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          Understand when automation might impact your field and get personalized adaptation strategies to future-proof your career.
                        </p>
                        <div className="flex flex-col gap-2">
                          <Button asChild className="w-full">
                            <Link to="/ai-career-analysis">
                              <Zap className="h-4 w-4 mr-2 shrink-0" />
                              Get AI Risk Analysis
                            </Link>
                          </Button>
                          <Button variant="outline" asChild className="w-full">
                            <Link to="/ai-economy-jobs">
                              Explore AI Economy Jobs
                            </Link>
                          </Button>
                          <Button variant="outline" asChild className="w-full">
                            <Link to="/resources/ai-career-planning">
                              Learn More
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <RelatedMetrics
                    occupation={{
                      title: final_occupation_title,
                      occ_title: final_occupation_title,
                      soc: final_occupation_code,
                      soc_code: final_occupation_code,
                    }}
                    location={location}
                    currentMetric="trends"
                    variant="compact"
                  />
                </>
              )}
              
              <WatchlistManager />
              
              <AdSlot slot="sidebar" />
            </div>
          </div>
        </>
      ) : null}

      <AdSlot slot="in-content" />
      </div>
    </div>
  );
};

export default TrendsCenter;